import * as React from "react"
import { cn } from "@/lib/utils"

export interface AutoGrowTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  /**
   * Trap Enter. For fields that hold ONE value (a section title, a key point)
   * a newline is never meaningful, and letting one in silently changes the
   * stored string as well as the height.
   */
  singleLine?: boolean
}

/**
 * A textarea that is exactly as tall as its content — no scrollbar, no fixed
 * height, no drag handle.
 *
 * Why this exists rather than `<Input>`: it is the editing half of a
 * reveal-on-hover row (see `PlanSectionRow` in `PlanCard.tsx`), whose resting
 * half is plain wrapped text. A single-line input forces the resting text to
 * one truncated line so the row does not change height on hover — which hides
 * the content this card exists to show. Growing to fit makes both halves wrap
 * identically, so the heights match by construction and nothing needs cutting.
 *
 * The height-matching contract is on the CALLER: the resting text must carry
 * the same font-size, line-height, horizontal padding and border width as the
 * textarea, in the same width slot. Change one, change both.
 */
const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  ({ className, value, singleLine = false, onKeyDown, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null)

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node
        if (typeof forwardedRef === "function") forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      },
      [forwardedRef]
    )

    const resize = React.useCallback(() => {
      const el = innerRef.current
      if (!el) return
      // Collapse first: `scrollHeight` reports the CURRENT height when the box
      // is already tall enough, so without this the field can only ever grow.
      el.style.height = "auto"
      // jsdom reports 0 for every layout metric. Writing "0px" there would pin
      // the field shut in tests for no benefit, so leave it alone.
      if (el.scrollHeight > 0) {
        // Tailwind's preflight sets `box-sizing: border-box`, so `height` must
        // cover the borders that `scrollHeight` excludes — otherwise the field
        // is 2px short and scrolls its last line.
        const style = window.getComputedStyle(el)
        const borders =
          parseFloat(style.borderTopWidth || "0") + parseFloat(style.borderBottomWidth || "0")
        el.style.height = `${el.scrollHeight + borders}px`
      }
    }, [])

    // Before paint, not after: a post-paint resize is a visible jump.
    React.useLayoutEffect(resize, [resize, value])

    // The AI panel is resizable, and a narrower box wraps to more lines. Without
    // this the field keeps the height it was born with and clips or gaps.
    React.useEffect(() => {
      const el = innerRef.current
      if (!el || typeof ResizeObserver === "undefined") return
      const observer = new ResizeObserver(resize)
      observer.observe(el)
      return () => observer.disconnect()
    }, [resize])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (singleLine && event.key === "Enter" && !event.shiftKey) event.preventDefault()
      onKeyDown?.(event)
    }

    return (
      <textarea
        ref={setRefs}
        rows={1}
        value={value}
        onKeyDown={handleKeyDown}
        className={cn("w-full resize-none overflow-hidden", className)}
        {...props}
      />
    )
  }
)
AutoGrowTextarea.displayName = "AutoGrowTextarea"

export { AutoGrowTextarea }
